import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableRowsIcon from '@mui/icons-material/TableRows';
import DashboardBarChart from '../charts/DashboardBarChart';
import DashboardStackedChart from '../charts/DashboardStackedChart';
import DashboardAnalyticsService from '../../services/DashboardAnalyticsService';
import PivotCrossTab from './PivotCrossTab';
import VolumePivotTable from './VolumePivotTable';

/**
 * ChartDetailModal — reusable full-size chart + data table popup.
 *
 * Props:
 *   open          — bool
 *   onClose       — fn
 *   title         — string
 *   subtitle      — string (optional)
 *   accentColor   — string hex (default Allianz blue)
 *   chartType     — 'bar' | 'stacked' (default 'bar')
 *   chartData     — array of objects (pivoted, Recharts-ready)
 *   xKey          — string (x-axis data key)
 *   series        — [{ dataKey, name, color }]
 *   tableData     — array of raw objects (for data table)
 *   tableColumns  — [{ key, label }]
 *   rawData       — array (passed to export service)
 *   exportName    — string (filename prefix)
 *   exportFormats — (optional) the formats offered, default ['csv', 'xlsx'];
 *                   one format gives a single "Download <FORMAT>" button
 *   showChart     — bool (default true): false shows only the data table,
 *                   with no Chart / Data Table toggle
 *   extraSheets   — (optional) [{ name, rows }] additional XLSX sheets
 *                   appended after the primary export -- CSV has no concept
 *                   of multiple sheets, so this is silently a no-op there
 *   pivotData      — (optional) raw flat array for PivotCrossTab (year×quarter)
 *   pivotStatusKey — (optional) field name for the status dimension in pivotData
 *   pivotType      — 'volume' | 'ratio' — renders VolumePivotTable (type×year)
 *   pivotStatusKeys — array of count field names to sum in VolumePivotTable
 *   xTickWrap, yDomain, yTickFormatter, minPointSize, showValueLabels, valueLabelFormatter
 *                  — forwarded as-is to DashboardBarChart (see its own prop
 *                    docs); undefined by default so existing callers are unaffected
 */
const ChartDetailModal = ({
  open,
  onClose,
  title = '',
  subtitle = '',
  accentColor = '#003781',
  chartType = 'bar',
  chartData = [],
  xKey = 'quarter',
  series = [],
  tableData = [],
  tableColumns = [],
  rawData = [],
  exportName = 'export',
  exportFormats = ['csv', 'xlsx'],
  showChart = true,
  extraSheets = [],
  pivotData = null,
  pivotStatusKey = 'request_status',
  pivotType = null,
  pivotStatusKeys = ['new_count', 'wip_count', 'completed_count'],
  xTickWrap,
  yDomain,
  yTickFormatter,
  minPointSize,
  showValueLabels,
  valueLabelFormatter,
}) => {
  const [view, setView] = useState(showChart ? 'chart' : 'table');
  const [exportFormat, setExportFormat] = useState(exportFormats[0]);
  // The dialog's accessible name is its title: there is no DialogTitle for MUI to point at.
  const titleId = React.useId();

  const handleExport = () => {
    DashboardAnalyticsService.downloadExport(rawData, exportName, exportFormat, extraSheets);
  };

  // Reset to chart view whenever the modal opens (or the table, if that is all there is)
  React.useEffect(() => {
    if (open) setView(showChart ? 'chart' : 'table');
  }, [open, showChart]);
  const rowCount = (
    <Chip
      label={`${tableData.length} rows`}
      size="small"
      sx={{ fontSize: 11, height: 22, bgcolor: '#eef0f4', color: '#6b7280' }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
          height: '75vh',
          maxHeight: '75vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Gradient header ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003781 0%, #0057A8 100%)',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography id={titleId} variant="h6" fontWeight={700} sx={{ color: 'white', lineHeight: 1.25 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            bgcolor: 'rgba(255,255,255,0.15)',
            ml: 2,
            mt: 0.25,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Toolbar ── */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: '1px solid #eef0f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8faff',
        }}
      >
        {/* View toggle -- or, with no chart, the row count in its place */}
        {!showChart ? rowCount : (
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => { if (v) setView(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              fontSize: 13,
              fontWeight: 500,
              borderColor: '#e0e3ea',
              color: '#6b7280',
              '&.Mui-selected': {
                color: accentColor,
                bgcolor: `${accentColor}14`,
                borderColor: `${accentColor}60`,
                fontWeight: 600,
              },
            },
          }}
        >
          <ToggleButton value="chart">
            <BarChartIcon fontSize="small" sx={{ mr: 0.75, fontSize: 16 }} />
            Chart
          </ToggleButton>
          <ToggleButton value="table">
            <TableRowsIcon fontSize="small" sx={{ mr: 0.75, fontSize: 16 }} />
            Data Table
          </ToggleButton>
        </ToggleButtonGroup>
        )}

        {/* Row count badge + export */}
        <Box display="flex" alignItems="center" gap={1.5}>
          {showChart && view === 'table' && rowCount}
          {exportFormats.length === 1 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={handleExport}
              disabled={!rawData.length}
              sx={{ textTransform: 'none', color: accentColor, borderColor: `${accentColor}60` }}
            >
              {`Download ${exportFormats[0].toUpperCase()}`}
            </Button>
          )}
          {exportFormats.length > 1 && (
          <ToggleButtonGroup
            value={exportFormat}
            exclusive
            onChange={(_, v) => { if (v) setExportFormat(v); }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 1.5,
                fontSize: 12,
                borderColor: '#e0e3ea',
                color: '#6b7280',
                '&.Mui-selected': {
                  color: accentColor,
                  bgcolor: `${accentColor}14`,
                  fontWeight: 600,
                },
              },
            }}
          >
            {exportFormats.map((format) => (
              <ToggleButton key={format} value={format}>{format.toUpperCase()}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          )}
          {exportFormats.length > 1 && (
          <Tooltip title="Download data">
            <IconButton
              size="small"
              onClick={handleExport}
              sx={{
                color: accentColor,
                bgcolor: `${accentColor}12`,
                '&:hover': { bgcolor: `${accentColor}22` },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Content area ── */}
      <Box
        sx={{
          p: 3,
          overflow: 'auto',
          flex: 1,
        }}
      >
        {view === 'chart' ? (
          chartType === 'stacked' ? (
            <DashboardStackedChart
              data={chartData}
              xKey={xKey}
              series={series}
              height={440}
            />
          ) : (
            <DashboardBarChart
              data={chartData}
              xKey={xKey}
              series={series}
              height={440}
              xTickWrap={xTickWrap}
              yDomain={yDomain}
              yTickFormatter={yTickFormatter}
              minPointSize={minPointSize}
              showValueLabels={showValueLabels}
              valueLabelFormatter={valueLabelFormatter}
            />
          )
        ) : pivotType ? (
          <VolumePivotTable
            data={tableData}
            mode={pivotType}
            statusKeys={pivotStatusKeys}
            accentColor={accentColor}
          />
        ) : pivotData ? (
          <PivotCrossTab
            data={pivotData}
            statusKey={pivotStatusKey}
            accentColor={accentColor}
          />
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            // The table scrolls inside its own frame, so its sticky header has something to stick to.
            // border-box: the border fits inside that 100%, or the content area scrolls 2px as well.
            sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'auto', maxHeight: '100%', boxSizing: 'border-box' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: '#374151',
                        bgcolor: '#f4f6fb',
                        borderBottom: `2px solid ${accentColor}35`,
                        py: 1.5,
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length}
                      align="center"
                      sx={{ py: 5, color: '#9ca3af', fontSize: 14 }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:nth-of-type(even)': { bgcolor: '#fafbff' },
                        '&:hover': { bgcolor: `${accentColor}08` },
                        transition: 'background 0.12s',
                      }}
                    >
                      {tableColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          sx={{ fontSize: 13, color: '#374151', py: 1.1, borderColor: '#f0f1f3' }}
                        >
                          {row[col.key] ?? '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Dialog>
  );
};

export default ChartDetailModal;
